import React from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Feature, InlineAlert, useFeatureFlag } from '@src/components'
import { UiInlineAlert } from '@src/types'
import { NavigationContainer } from '@src/screens/Common/containers'
import {
  cgmSteloImage,
  marketplaceByosImage,
  marketplaceCoachSupportImage,
  marketplaceIphoneWAppImage,
  marketplaceNNImage,
} from '@src/assets/images'
import { ProductSummary } from '@src/screens/Marketplace/components/NewMarketplace/ProductSummary'
import { Text } from '@src/components/base'

interface BaseSubscriptionOptionsProps {
  title: string
  onOwnSensorPress: () => void
  onPurchaseSensorPress: () => void
  onVideoCallsPress: () => void
  goBack?: () => void
}

export const BaseSubscriptionOptions = (props: BaseSubscriptionOptionsProps) => {
  const { title, onOwnSensorPress, onPurchaseSensorPress, onVideoCallsPress, goBack } = props
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()

  const newProgramSelectionContent = useFeatureFlag(Feature.NewProgramSelectionContent)

  return (
    <NavigationContainer title={title} goBack={goBack}>
      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        bounces={false}
      >
        <Text type="large" style={styles.description}>
          Pick the option that best fits your needs.
        </Text>
        {newProgramSelectionContent ? (
          <View style={styles.productsContainer}>
            <ProductSummary
              title="Glucose Programs"
              description="Get real-time insights with a CGM and personalized support from nutrition coach."
              image={marketplaceIphoneWAppImage}
              imageStyle={styles.glucoseProgramsImage}
              onPress={onPurchaseSensorPress}
            />
            <ProductSummary
              title="Bring My Own CGM"
              description="Easily connect your existing CGM and start tracking with the Nutrisense app."
              image={cgmSteloImage}
              imageStyle={styles.byosImage}
              onPress={onOwnSensorPress}
            />
            <ProductSummary
              title="1:1 Coach Support"
              description="Instant support from licensed nutritionists and stress coaches."
              image={marketplaceCoachSupportImage}
              imageStyle={styles.coachImage}
              onPress={onVideoCallsPress}
            />
          </View>
        ) : (
          <>
            <View style={styles.productsContainer}>
              <ProductSummary
                title="Glucose Programs"
                description="Optimize your health with glucose monitoring and dietitian support."
                image={marketplaceIphoneWAppImage}
                imageStyle={styles.oldGlucoseProgramsImage}
                onPress={onPurchaseSensorPress}
              />
              <ProductSummary
                title="Bringing My Own CGM"
                description="Activate your own sensor seamlessly with the Nutrisense app."
                image={marketplaceByosImage}
                imageStyle={styles.oldByosImage}
                onPress={onOwnSensorPress}
              />
              <ProductSummary
                title="Nutritionist Video Calls"
                description="In-network, in-the-moment video consultations with expert nutritionists."
                image={marketplaceNNImage}
                onPress={onVideoCallsPress}
              />
            </View>
            <InlineAlert
              category={UiInlineAlert.Neutral}
              // eslint-disable-next-line max-len
              message="Your Program might include a CGM (Continuous Glucose Monitor) or Glucose Biosensor. These are medical devices that continuously track glucose levels in real-time."
            />
          </>
        )}
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  content: {
    flexGrow: 1,
    paddingTop: 8,
    marginHorizontal: 16,
  },
  description: {
    marginBottom: 24,
    textAlign: 'center',
  },
  productsContainer: {
    flex: 1,
    gap: 16,
    marginBottom: 16,
  },
  glucoseProgramsImage: {
    marginRight: 18,
  },
  byosImage: {
    width: 101,
    height: 88,
    marginBottom: 20,
  },
  coachImage: {
    width: 124,
    height: 123,
    marginRight: 12,
  },
  oldGlucoseProgramsImage: {
    marginRight: 32,
  },
  oldByosImage: {
    alignSelf: 'center',
  },
})
